import React, {useCallback, useEffect} from 'react';
import clsx from 'clsx';
import clamp from 'lodash/clamp';
import ResizeObserver from 'resize-observer-polyfill';

import {rafRetry} from 'common/util/dom';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {commaizePartialNumber, MeasureUtil} from 'common/util';
import {useMountEffect} from 'common/hooks/useMountEffect';
import {useUnmount} from 'common/hooks/useUnmount';
import {usePrevious} from 'common/hooks/usePrevious';
import {withStyles} from 'common/util/react';

import {SvgIconReact} from '../svg-icon';
import {TooltipReact} from '../tooltip';
import {ButtonReact} from '../button';
import {ButtonSize, ButtonTheme} from '../button/constants';
import {SpinnerReact} from '../spinner';
import {SvgIconType} from '../svg-icon/types';
import {
  AutocompleteType,
  InputProps,
  InputSize,
  InputTheme,
  InputType,
  Necessary,
  TextAlign,
  ValidationStatus,
} from './types';
import './label';
import {InputLabelReact} from './label';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

const InputComponent = React.forwardRef<any, InputProps>(
  (
    {
      autocomplete = AutocompleteType.Off,
      autoFocus = false,
      icon = ``,
      label = ``,
      minInputWidth = null,
      necessary = Necessary.Default,
      onBlur,
      onChange,
      onClear,
      onClick,
      onFocus,
      onInput,
      onKeydown,
      onKeyup,
      onPaste,
      size = InputSize.Large,
      textAlign = TextAlign.Start,
      theme = InputTheme.Default,
      type = InputType.Text,
      validationStatus = ValidationStatus.Default,
      ...props
    },
    forwardedRef,
  ) => {
    const [textWidth, setTextWidth] = React.useState<number>(0);
    // Coerce null to "" — passing null as the value prop on a native <input> triggers a React warning
    const [value, setValue] = React.useState<string>(props.value || ``); // Make this a ref
    const inputWrapper = React.useRef<HTMLDivElement>(null);
    const searchInput = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const previousAutoFocus = usePrevious(autoFocus);
    const prefixNode = React.useRef<HTMLDivElement>(null);
    const prefixResizeObserver = React.useRef<ResizeObserver | null>(null);

    React.useImperativeHandle(forwardedRef, () => searchInput.current, []);

    const select = useCallback(() => {
      return searchInput.current?.select();
    }, [searchInput]);

    const focus = useCallback(async () => {
      await rafRetry(() => {
        if (!searchInput.current) {
          return false;
        }
        searchInput.current.focus();
        if (searchInput.current.parentElement) {
          searchInput.current.parentElement.scrollLeft = searchInput.current.parentElement.scrollWidth;
        }
        return Boolean(searchInput.current);
      });
    }, [searchInput]);

    /**
     * For auto resizing inputs, makes sure that the scrolling area is
     * shifted to the right as the user types or content grows.
     *
     * This behavior mimics the native input tag with overflowing content.
     */
    const updateInputWidth = useCallback(
      (inputWidthValue: string | null = value) => {
        if (!props.multilinePrefix) {
          return;
        }

        inputWidthValue ||= props.placeholder ?? null;
        if (inputWidthValue) {
          const currentTextWidth = textWidth ?? 0;

          const fontSizeMap = {
            [InputSize.Large]: mpFonts.fontSizeLarge,
            [InputSize.Medium]: mpFonts.fontSizeMedium,
            [InputSize.Small]: mpFonts.fontSizeSmall,
          };

          const updatedTextWidth =
            MeasureUtil.measureTextWidth(inputWidthValue, `400 ${fontSizeMap[size]} ${mpFonts.fontFamilyPrimary}`) + 16;

          setTextWidth(updatedTextWidth);
          if ((searchInput.current && updatedTextWidth > currentTextWidth) ?? 0) {
            rafRetry(() => {
              if (!searchInput.current || !searchInput.current.parentElement) {
                return false;
              }
              searchInput.current.parentElement.scrollLeft = searchInput.current.parentElement.scrollWidth;
              return true;
            });
          }
        } else {
          setTextWidth(0);
        }
      },
      [props.multilinePrefix, props.placeholder, size, textWidth, value],
    );

    useMountEffect(() => {
      if (autoFocus) {
        focus();
      }
      if (props.autoSelect) {
        select();
      }
    });

    useUnmount(() => {
      prefixResizeObserver.current?.disconnect();
    });

    useEffect(() => {
      const newValue = props.value;
      const oldValue = value;
      // null is treated the same as undefined — passing null to a native <input> triggers a React warning
      const isNoOpUpdate = newValue === undefined || newValue === null || newValue === oldValue;
      // For number inputs, the parent's normalized form (e.g. "0") would otherwise
      // clobber an in-progress raw input (e.g. "0." or "1.50"), breaking decimal typing.
      const isNumberRoundTrip =
        type === InputType.Number && newValue !== `` && oldValue !== `` && Number(newValue) === Number(oldValue);
      if (isNoOpUpdate || isNumberRoundTrip) {
        return;
      }
      setValue(newValue);
    }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (autoFocus && previousAutoFocus !== autoFocus) {
        focus();
      }
    }, [focus, previousAutoFocus, autoFocus]);

    useEffect(() => {
      updateInputWidth();
    }, [props.placeholder, updateInputWidth]);

    const supportsMinMaxAttrs = () => {
      return type === InputType.Number;
    };

    const parseCommifiedNumber = useCallback((v: string): string => {
      // Don't default an empty input to 0.
      if (v === ``) {
        return ``;
      }
      return v.replace(/,/g, ``);
    }, []);

    const changeValue = useCallback(
      (changedValue: string): string => {
        updateInputWidth(changedValue);
        let updatedValue = changedValue;
        if (type === InputType.Number) {
          // Remove commas so we're always storing a raw number with the input.
          updatedValue = parseCommifiedNumber(changedValue);
        }
        setValue(updatedValue);
        return updatedValue;
      },
      [type, updateInputWidth, parseCommifiedNumber],
    );

    const handleInput = useCallback(
      (ev: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>): void => {
        const target = ev.target as HTMLInputElement | HTMLTextAreaElement;
        const updatedValue = changeValue(target.value);
        onInput?.(updatedValue, ev);
      },
      [changeValue, onInput],
    );

    const handleKeydown = (ev: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeydown?.(ev);
    };

    const handleKeyup = (ev: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyup?.(ev);
    };

    const handlePaste = (
      ev: React.ClipboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLTextAreaElement>,
    ): void => {
      onPaste?.(ev);
    };

    const handleChange = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>): void => {
        ev.stopPropagation();
        let changedValue = ev.target.value;
        if (type === InputType.Number) {
          let [min, max] = [Number.parseFloat(props.min!), Number.parseFloat(props.max!)];
          if (Number.isNaN(min)) {
            min = -Number.MAX_VALUE;
          }
          if (Number.isNaN(max)) {
            max = Number.MAX_VALUE;
          }
          // First remove commas
          changedValue = parseCommifiedNumber(changedValue);
          // Then clamp
          changedValue = String(clamp(Number.parseFloat(changedValue), min, max));
          // need to update the internal state value here. otherwise the prop and state is out of sync
          changeValue(changedValue);
        }
        onChange?.(changedValue, ev);
        // Note: Do NOT blur the input here; if a widget needs to blur-on-change,
        // it should be done in the widget itself as a special case to avoid
        // breaking browser-standard behavior by default.
      },
      [onChange, changeValue, parseCommifiedNumber, type, props.min, props.max],
    );

    const handleBlur = useCallback(
      (ev: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>): void => {
        ev.stopPropagation();
        handleChange(ev);
        onBlur?.(ev);
      },
      [handleChange, onBlur],
    );

    const handleClearInput = useCallback(
      (ev: React.MouseEvent) => {
        ev.stopPropagation();
        changeValue(``);
        onChange?.(``, ev as any);
        onClear?.(ev);
        // without this, we focus on mp-input instead of just the input field. focusing on
        // mp-input prevents us from typing our second query, while focusing on the searchInput
        // allows to type something else.
        focus();
      },
      [changeValue, focus, onChange, onClear],
    );

    const handleClick = (ev: React.MouseEvent<HTMLInputElement> | React.MouseEvent<HTMLTextAreaElement>): void => {
      onClick?.(ev);
    };

    const handleSelectOnFocus = () => {
      const searchInputCurrent = searchInput.current;
      if (props.selectOnFocus && searchInputCurrent) {
        const alreadyHasFocus = document.activeElement === searchInputCurrent;
        const selectionLength = (searchInputCurrent.selectionEnd ?? 0) - (searchInputCurrent.selectionStart ?? 0);
        if (!alreadyHasFocus && !selectionLength) {
          // Don't select on focus if we already have focus or a selection because this will
          // make it so the current text cursor position or selection is overridden.
          select();
        }
      }
    };

    const handleFocus = (ev: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>): void => {
      handleSelectOnFocus();
      if (props.selectOnFocus) {
        select();
      }
      onFocus?.(ev);
    };

    const displayValue = (): string => {
      if (type === InputType.Number) {
        return commaizePartialNumber(value);
      }
      return value;
    };

    useEffect(() => {
      // don't think this is needed because we should just enter this codepath once? but just in case
      prefixResizeObserver.current?.disconnect();
      if (prefixNode.current) {
        prefixResizeObserver.current = new ResizeObserver(() => {
          if (inputWrapper.current) {
            const visibleInputWidth =
              inputWrapper.current.getBoundingClientRect().right -
              (searchInput.current?.getBoundingClientRect().left ?? 0);
            // add a buffer to visible input width to account for off by one type issues
            const bufferPixels = 5;
            if (visibleInputWidth + bufferPixels < (minInputWidth ?? 0)) {
              inputWrapper.current.scrollLeft += (minInputWidth ?? 0) - visibleInputWidth;
            }
          }
        });
        prefixResizeObserver.current.observe(prefixNode.current);
      }
    }, [inputWrapper, prefixNode, minInputWidth]);

    // Called as a function, not rendered as a component: a component declared here would be a new type on
    // every render and remount the icons, replacing a clear button between a click's mousedown and mouseup.
    const renderRightIcon = () => {
      if (props.hideRightIcon) {
        return null;
      }
      if (props.rightIcon) {
        return (
          <div className={clsx(styles.mpInputRightIcon, styles.customRightIcon)}>
            <SvgIconReact icon={props.rightIcon} />
            {props.helpText ? <TooltipReact text={props.helpText} /> : null}
          </div>
        );
      } else if ((validationStatus && validationStatus === `valid`) || validationStatus === `invalid`) {
        const rightIconToUse: SvgIconType = {valid: `circle-checkmark` as const, invalid: `warning` as const}[
          validationStatus
        ];
        return (
          <div
            className={clsx(styles.mpInputRightIcon, {
              [styles.valid]: validationStatus === `valid`,
              [styles.invalid]: validationStatus === `invalid`,
            })}
          >
            <SvgIconReact icon={rightIconToUse} />
          </div>
        );
      } else if (props.alwaysShowClear || (value && props.showClear)) {
        return (
          <div
            elref='clearButton'
            className={clsx(styles.mpInputRightIcon, styles.clickable)}
            onClick={(ev) => handleClearInput(ev)}
          >
            <SvgIconReact icon={`circle-clear`} className={styles.mpInputClickableIcon} />
          </div>
        );
      } else if (props.preText && props.helpText) {
        return (
          <div className={styles.mpInputRightIcon}>
            <SvgIconReact icon={`info-xs`} />
            <TooltipReact text={props.helpText} />
          </div>
        );
      }
      return null;
    };

    function NumericControls() {
      const step = (delta: number) => (ev: React.MouseEvent) => {
        // mousedown + preventDefault on this wrapper keeps focus on the input
        // (ButtonReact is a panel2react wrapper and does not forward onMouseDown,
        // so the handler must live on a plain DOM element).
        ev.preventDefault();
        const next = String(clamp(parseInt(value, 10) + delta, parseInt(props.min!, 10), parseInt(props.max!, 10)));
        setValue(next);
        onChange?.(next, ev as any);
      };
      return (
        <div className={styles.mpInputNumericArrowControls}>
          {/* Can dump this and move the handler onto button once ButtonReact is an actual React component w/o panel2react */}
          <div style={{display: `contents`}} onMouseDown={step(1)}>
            <ButtonReact
              className={styles.numericArrowButton}
              theme={ButtonTheme.Tertiary}
              size={ButtonSize.Tiny}
              icon={`caret-up`}
            />
          </div>
          <div style={{display: `contents`}} onMouseDown={step(-1)}>
            <ButtonReact
              className={styles.numericArrowButton}
              theme={ButtonTheme.Tertiary}
              size={ButtonSize.Tiny}
              icon={`caret-down`}
            />
          </div>
        </div>
      );
    }

    const isValid = validationStatus === ValidationStatus.Valid;
    const isInvalid = validationStatus === ValidationStatus.Invalid;

    return (
      <div
        className={clsx(styles.mpInputContainer, props.className, {
          [styles.themeWhite]: theme === `white`,
          [styles.isReadonly]: props.readonly,
        })}
        elref={props.elref}
        style={props.style}
      >
        {label ? (
          <div className={styles.inputLabel} onClick={() => props.disabled === false && focus()}>
            <InputLabelReact
              className={styles.inputLabel}
              label={label}
              fieldId={props.fieldId}
              necessary={necessary}
              helpText={props.helpText}
              extraInfo={props.extraInfo}
            />
          </div>
        ) : null}
        <div
          className={clsx(styles.mpInputFieldContainer, {
            [styles.hasMinInputWidth]: Boolean(minInputWidth),
            [styles.hasMultilinePrefix]: props.multilinePrefix,
            [styles.hideFocusStyling]: props.hideFocusStyling,
            [styles.isDisabled]: props.disabled,
            [styles.sizeSmall]: size === `small`,
            [styles.sizeMedium]: size === `medium`,
            [styles.sizeLarge]: size === `large`,
            [styles.typeNumber]: type === `number`,
            [styles.typePassword]: type === `password`,
            [styles.typeText]: type === `text`,
            [styles.isValid]: isValid,
            [styles.isInvalid]: isInvalid,
          })}
          onClick={() => focus()}
        >
          {(() => {
            const TagName = type === `textarea` ? `textarea` : `input`;

            return (
              <>
                <div className={styles.mpInputLeftArea}>
                  {icon && (
                    <div className={`mp-input-left-icon`}>
                      {icon === `spinner` ? (
                        <SpinnerReact className={styles.mpInputLeftIcon} />
                      ) : (
                        <SvgIconReact icon={icon} className={styles.mpInputLeftIcon} />
                      )}
                    </div>
                  )}
                  {props.preText && (
                    <div
                      className={clsx(styles.mpInputPreText, {
                        [styles.sizeSmall]: size === `small`,
                        [styles.sizeMedium]: size === `medium`,
                        [styles.sizeLarge]: size === `large`,
                      })}
                      onClick={() => focus()}
                    >
                      {props.preText}
                    </div>
                  )}
                </div>
                <div className={styles.mpInputWrapper} ref={inputWrapper}>
                  {props.prefixRenderer &&
                    (props.multilinePrefix ? (
                      props.prefixRenderer()
                    ) : (
                      <div ref={prefixNode}>{props.prefixRenderer()}</div>
                    ))}
                  <TagName
                    ref={searchInput as any} // Feels like overloading tags to one component is hacky at best
                    className={styles.mpInput}
                    style={{
                      textAlign,
                      ...(minInputWidth !== null && {minWidth: `${minInputWidth}px`}),
                      ...(props.multilinePrefix && {
                        width: textWidth ? `${textWidth}px` : undefined,
                      }),
                    }}
                    aria-activedescendant={props.ariaActiveDescendant}
                    aria-controls={props.ariaControls}
                    aria-expanded={props.ariaExpanded}
                    aria-label={props.ariaLabel}
                    role={props.ariaRole}
                    autoComplete={autocomplete}
                    elref={`inputTag`}
                    id={props.fieldId}
                    name={props.fieldName}
                    tabIndex={props.tabIndex}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    readOnly={props.readonly}
                    size={1}
                    // We do custom comma formatting on number type inputs.
                    // Browsers won't accept something like '1,000' as a valid
                    // do not use the "type" attribute in this case.
                    type={TagName === `input` && type !== InputType.Number ? type : undefined}
                    maxLength={props.maxlength}
                    min={supportsMinMaxAttrs() ? props.min : undefined} // only enforces arrow key decrements
                    max={supportsMinMaxAttrs() ? props.max : undefined} // only enforces arrow key increments
                    step={supportsMinMaxAttrs() ? props.step : undefined} // only enforces arrow key increments
                    value={displayValue()}
                    onClick={(ev) => handleClick(ev)}
                    onInput={(ev) => handleInput(ev)}
                    onBlur={(ev) => handleBlur(ev)}
                    onFocus={(ev) => handleFocus(ev)}
                    // Got rid of onChange because in React-land onChange just
                    // gets called on every `input` event instead of when a value
                    // is actually committed (e.g. on blur) like native inputs.
                    // onChange={(ev) => handleChange(ev)}
                    onKeyDown={(ev) => handleKeydown(ev)}
                    onKeyUp={(ev) => handleKeyup(ev)}
                    onPaste={(ev) => handlePaste(ev)}
                  />
                </div>
                <div className={styles.mpInputRightArea}>
                  {props.suffixRenderer?.()}
                  {props.actionText && (props.alwaysShowActionText || value) ? (
                    <div className={styles.mpInputActionText}>{props.actionText}</div>
                  ) : null}
                  {type === InputType.Number && props.showNumericInputControls && <NumericControls />}
                  {renderRightIcon()}
                </div>
              </>
            );
          })()}
        </div>
        {validationStatus === `invalid` && props.errorMessage ? (
          <div elref={`inputErrorMessage`} className={styles.mpInputErrorMessage}>
            {props.errorMessage}
          </div>
        ) : null}
      </div>
    );
  },
);

export const InputReact = withStyles(InputComponent, css, {eagerMount: true});
